import * as React from "react";
import { useState } from "react";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { skipToken } from "@tanstack/react-query";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import SelectableCard from "@v1/components/designSystem/card/SelectableCard";
import { DeprecatedEmptyState } from "@v1/components/designSystem/empty-state/EmptyState";
import { Modal } from "@v1/components/designSystem/modal/Modal";
import Selectable from "@v1/components/designSystem/Selectable";
import useOnSubmitTemplateAction from "@v1/hooks/useOnSubmitTemplateAction";
import { componentTemplatesRouter } from "@v1/trpc/routes/componentTemplates";
import { projectRouter } from "@v2/trpc/routes/project";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { getProjectName } from "@v2/utils/project-utils";
import { formatDistanceToNow } from "date-fns";
import { Building2, FolderPlus } from "lucide-react";
import { useParams } from "react-router-dom";

const ProjectSelection: React.FC<{
  selectedProjectId: string | null;
  onChange: (projectId: string | null) => void;
  workspaceId: string | null;
}> = ({ selectedProjectId, onChange, workspaceId }) => {
  const { data: projectsData, isLoading: isLoadingProjects } =
    projectRouter.listWithStats.useQuery(
      workspaceId ? { workspaceId } : skipToken,
    );

  if (isLoadingProjects) {
    return (
      <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto pr-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-[72px] w-full rounded-lg" />
        ))}
      </div>
    );
  }

  // NOTE (Varun, 2025-12-04): Component templates are only compatible with V1
  // projects (hostingType="shopify") which use Elements. V2 projects
  // (hostingType="replo") use Files and have their own template system.
  const v1Projects = projectsData?.projects.filter(
    (project) => project.hostingType === "shopify",
  );

  if (!v1Projects || v1Projects.length === 0) {
    return (
      <DeprecatedEmptyState
        title={workspaceId ? "No Shops Found" : "Select a workspace"}
        description={
          workspaceId
            ? "This workspace doesn't have any shops."
            : "Select a workspace above to import this template."
        }
        icon={<FolderPlus />}
        variant="primary"
        size="sm"
        UNSAFE_className="py-[15%]"
      />
    );
  }

  return (
    <div className="flex max-h-[200px] w-full flex-col gap-2 overflow-y-auto">
      {v1Projects.map((project) => (
        <SelectableCard
          size="base"
          key={project.id}
          title={getProjectName(project)}
          description={`Edited ${formatDistanceToNow(project.lastEditedAt ?? project.createdAt)} ago`}
          layoutClassName="w-full"
          isActive={selectedProjectId === project.id}
          onClick={() => onChange(project.id)}
        />
      ))}
    </div>
  );
};

const WorkspaceSelector: React.FC<{
  workspaces: { name: string; id: string }[];
  selectedWorkspaceId: string | null;
  onChange: (workspaceId: string) => void;
  isLoading?: boolean;
}> = ({ selectedWorkspaceId, onChange, isLoading = false, workspaces }) => {
  const workspaceOptions = React.useMemo(() => {
    return workspaces.map((workspace) => ({
      label: workspace.name,
      value: workspace.id,
    }));
  }, [workspaces]);

  if (isLoading) {
    return <Skeleton className="h-10 w-full rounded-sm" />;
  }

  if (workspaces.length === 0) {
    return (
      <div className="text-muted py-2 text-sm">
        No workspaces available. Please create a workspace first.
      </div>
    );
  }

  return (
    <Selectable
      placeholder="Select a workspace"
      options={workspaceOptions}
      value={selectedWorkspaceId}
      onSelect={onChange}
      size="base"
      areOptionsSearchable
      triggerStartEnhancer={<Building2 size={16} className="text-muted" />}
      layoutClassName="w-full"
    />
  );
};

export function SharedTemplateDetailsModal({
  onClose,
}: {
  onClose?: () => void;
}) {
  const { templateId } = useParams<{ templateId: string }>();
  const [isOpen, setIsOpen] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = React.useState<
    string | null
  >(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState<
    string | null
  >(null);
  const { onSubmit: onSubmitTemplateAction, isLoadingSubmitTemplate } =
    useOnSubmitTemplateAction();
  const { data: componentTemplate, isLoading: isLoadingComponentTemplate } =
    componentTemplatesRouter.get.useQuery(
      templateId
        ? {
            templateId,
          }
        : skipToken,
    );
  const { data, isPending: isLoadingWorkspaces } =
    workspaceRouter.getUserWorkspacesList.useQuery();
  const workspaces = data?.workspaces ?? [];

  const handleWorkspaceSelect = (workspaceId: string) => {
    setSelectedWorkspaceId(workspaceId);
    setSelectedProjectId(null);
  };

  const onSubmit = async () => {
    if (componentTemplate && selectedProjectId) {
      await onSubmitTemplateAction({
        template: componentTemplate,
        elementType: "page",
        projectId: selectedProjectId,
      });
    }
  };

  const handleOnOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open && onClose) {
      onClose();
    }
  };

  const isLoading = isLoadingComponentTemplate || isLoadingWorkspaces;

  return (
    <Modal
      title="Where will you use this template?"
      isOpen={isOpen}
      onOpenChange={handleOnOpenChange}
      size="lg"
      footer={
        <div className="flex gap-2">
          <DeprecatedButton
            variant="secondary"
            size="base"
            onClick={() => {
              handleOnOpenChange(false);
            }}
          >
            Cancel
          </DeprecatedButton>
          <DeprecatedButton
            variant="primary"
            type="submit"
            size="base"
            disabled={!selectedProjectId}
            isLoading={isLoadingSubmitTemplate}
            onClick={() => void onSubmit()}
          >
            Add to Shop
          </DeprecatedButton>
        </div>
      }
    >
      {(() => {
        if (isLoading) {
          return (
            <div className="flex h-full w-full gap-5">
              <div className="border-0.5 border-border flex h-full w-1/2 flex-col rounded-sm shadow-lg">
                <div className="bg-neutral-soft group relative gap-2 rounded-t px-2 pb-0 pt-3">
                  <div className="aspect-video w-full overflow-hidden">
                    <Skeleton className="h-full w-full rounded-sm" />
                  </div>
                </div>
                <div className="border-0.5 border-border rounded-b border-t bg-white p-3">
                  <Skeleton className="h-6 w-3/4 rounded-sm" />
                </div>
              </div>
              <div className="flex h-full w-1/2 flex-col gap-4">
                <Skeleton className="h-10 w-full rounded-sm" />
                <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto pr-2">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      className="h-[72px] w-full rounded-lg"
                    />
                  ))}
                </div>
              </div>
            </div>
          );
        }

        if (!componentTemplate) {
          return (
            <DeprecatedEmptyState
              title="Template Not Found"
              description="The template you're looking for could not be found."
              icon={<FolderPlus />}
              variant="primary"
              size="sm"
              UNSAFE_className="py-[15%]"
            />
          );
        }

        return (
          <div className="flex h-full w-full gap-5">
            {/* Left side - Template preview */}
            <div className="border-0.5 border-border flex h-full w-1/2 flex-col rounded-sm shadow-lg ">
              <div className="bg-neutral-soft group relative gap-2 rounded-t px-2 pb-0 pt-3 aspect-video w-full overflow-hidden">
                <img
                  src={componentTemplate.desktopPreviewUrl}
                  className="w-full object-contain object-top transition-opacity duration-300"
                  alt="Template Preview"
                />
              </div>

              <div className="border-0.5 border-border text-xs font-semibold rounded-b border-t bg-white p-3">
                {componentTemplate.name}
              </div>
            </div>

            {/* Right side - workspace and project selection */}
            <div className="flex h-full w-1/2 flex-col gap-2">
              <WorkspaceSelector
                workspaces={workspaces}
                selectedWorkspaceId={selectedWorkspaceId}
                onChange={handleWorkspaceSelect}
              />

              <ProjectSelection
                selectedProjectId={selectedProjectId}
                onChange={setSelectedProjectId}
                workspaceId={selectedWorkspaceId}
              />
            </div>
          </div>
        );
      })()}
    </Modal>
  );
}
